import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';
import { IntlShape } from 'react-intl';
import { fabric } from 'fabric';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { translateWithFallback } from '../../util';
import {
  CharacterIcon,
  CharacterRole,
  mapCharacterIconToFilePath,
} from '../SceneCanvas';
import { EvoachButton } from '../Button';
import { SendContext } from '../../SendContext';

import { FullScreenToggleButton } from './FullscreenToggle';

// taken from this icon: @mui/icons-material/Flip
const flipIcon =
  "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='iso-8859-1'%3F%3E%3Csvg version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='50%' y='50%' width='21px' height='21px' xml:space='preserve'%3E%3Ccircle cx='50%' cy='50%' r='50%' fill='%234199AC'/%3E%3Cpath transform='translate(2, 2)scale(0.7, 0.7)' style='fill:%23FFFFFF;' d='M15 21h2v-2h-2v2zm4-12h2V7h-2v2zM3 5v14c0 1.1.9 2 2 2h4v-2H5V5h4V3H5c-1.1 0-2 .9-2 2zm16-2v2h2c0-1.1-.9-2-2-2zm-8 20h2V1h-2v22zm8-6h2v-2h-2v2zM15 5h2V3h-2v2zm4 8h2v-2h-2v2zm0 8c1.1 0 2-.9 2-2h-2v2z'/%3E%3C/svg%3E%0A";

const svgRotateIcon = encodeURIComponent(`
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_d)">
      <circle cx="9" cy="9" r="5" fill="white"/>
      <circle cx="9" cy="9" r="4.75" stroke="black" stroke-opacity="0.3" stroke-width="0.5"/>
    </g>
      <path d="M10.8047 11.1242L9.49934 11.1242L9.49934 9.81885" stroke="black" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M6.94856 6.72607L8.25391 6.72607L8.25391 8.03142" stroke="black" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M9.69517 6.92267C10.007 7.03301 10.2858 7.22054 10.5055 7.46776C10.7252 7.71497 10.8787 8.01382 10.9517 8.33642C11.0247 8.65902 11.0148 8.99485 10.9229 9.31258C10.831 9.63031 10.6601 9.91958 10.4262 10.1534L9.49701 11.0421M8.25792 6.72607L7.30937 7.73554C7.07543 7.96936 6.90454 8.25863 6.81264 8.57636C6.72073 8.89408 6.71081 9.22992 6.78381 9.55251C6.8568 9.87511 7.01032 10.174 7.23005 10.4212C7.44978 10.6684 7.72855 10.8559 8.04036 10.9663" stroke="black" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
    <defs>
    <filter id="filter0_d" x="0" y="0" width="18" height="18" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/>
      <feOffset/>
      <feGaussianBlur stdDeviation="2"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0.137674 0 0 0 0 0.190937 0 0 0 0 0.270833 0 0 0 0.15 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
    </filter>
    </defs>
  </svg>
  `);
const rotateIcon = `data:image/svg+xml;utf8,${svgRotateIcon}`;

const CANVAS_WIDTH = 500;
const CANVAS_HEIGHT = 312;
const CHARACTER_SCALE = 0.15;
const SMALLSCREENVIEWPORT = [1, 0, 0, 1, 0, 0];
//let staticCanvas: fabric.StaticCanvas;
export interface CanvasEditorComponent extends BaseComponent {
  type: 'canvasEditor';
  props: CanvasEditorProps;
}

export interface CharacterProps {
  type?: string;
  value?: string;
  name: string;
  icon: CharacterIcon;
  role: CharacterRole[];
  statement: string;
  flipX?: boolean;
  flipY?: boolean;
  width?: number;
  height?: number;
  scale?: number;
  angle?: number;
  position?: {
    x?: number;
    y?: number;
  };
}
export interface CanvasEditorProps {
  initialCharacters: CharacterProps[];
  buttonText: string;
  saveResultTo?: string;
  isEcho?: boolean;
  tooltipFullscreenMessage?: string;
}
export const CanvasEditor: React.FC<CanvasEditorProps> = ({
  initialCharacters,
  buttonText,
  saveResultTo = 'canvasObjects',
  isEcho = false,
  tooltipFullscreenMessage,
}) => {
  const { send } = useContext(SendContext);
  var flipImg = document.createElement('img');
  var rotateImg = document.createElement('img');
  // Update ID of echo mode is changing. This is necessary to instantiate
  // a new fabric object. Without that change, React wouldn't distinguish between
  // a editable and an isEcho control as the internal canvas ID wouldnt change.
  const canvasID = useMemo(() => {
    return 'canvas' + isEcho + Math.round(Math.random() * 100000000);
  }, [isEcho]);

  rotateImg.src = rotateIcon;
  flipImg.src = flipIcon;
  var currentText: any;
  currentText = null;
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // remeber canvas directly as HTML object and not as React controlled element
  const canvas: any = useRef(); //fabric.Canvas | fabric.StaticCanvas

  // This Array is to track how the fabric stack changed in relation to the initialCharacter Array
  const setInitialIndexArray = () => {
    let indexArray = [];
    for (let index = 0; index < initialCharacters.length; index++) {
      indexArray.push(index);
    }
    return indexArray;
  };

  const [resortedCharacterIndex, setResortedCharacterIndex] = useState<
    number[]
  >(setInitialIndexArray());

  // This Funktion calculates the default Position of each Character depending on the number of characters
  const setCharacterPositions = (width: number, height: number) => {
    const charCount = initialCharacters.length;
    let firstX = width / 5;
    const firstY = height / (Math.round(charCount / 4) + 2);

    // if there are positions available, take these
    if (charCount > 0 && initialCharacters[0].position !== undefined) {
      return initialCharacters.map((char: CharacterProps, index: number) => {
        return {
          x: char.position?.x ?? index * firstX,
          y: char.position?.y ?? height / 2,
        };
      });
    } else {
      // no positions availble, do magic ordering

      if (initialCharacters.length <= 4) {
        firstX = width / (charCount + 1);
        return [
          {
            x: firstX,
            y: height / 2,
          },
          {
            x: firstX * 2,
            y: height / 2,
          },
          {
            x: firstX * 3,
            y: height / 2,
          },
          {
            x: firstX * 4,
            y: height / 2,
          },
        ];
      } else {
        let characterPositions = [];
        for (let index = 0; index < charCount; index++) {
          let currX = firstX * ((index % 4) + 1);
          let currY = firstY * (Math.floor(index / 4) + 1);
          characterPositions.push({
            x: currX,
            y: currY,
          });
        }
        return characterPositions;
      }
    }
  };
  const characterPositions = setCharacterPositions(CANVAS_WIDTH, CANVAS_HEIGHT);

  const smallScreenStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
    alignItems: 'flex-start',
  };
  const fullScreenStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    left: '10px',
    top: '10px',
    zIndex: '10000',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.5)',
  };

  const toggleFullscreen = (fullscreen: boolean) => {
    let newWidth;
    let newHeight;
    let newViewport;

    if (fullscreen) {
      //scale with aspect ratio

      const windowHeight = Math.max(
        document.documentElement.clientHeight || 0,
        window.innerHeight || 0
      );

      const windowWidth = Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
      );

      const widthScale = windowWidth / CANVAS_WIDTH;
      const heightScale = windowHeight / CANVAS_HEIGHT;

      //take the smaller scaling to keep the aspect ratio
      let scale =
        widthScale > heightScale ? heightScale - 0.2 : widthScale - 0.2;

      newWidth = scale * CANVAS_WIDTH;
      newHeight = scale * CANVAS_HEIGHT;
      newViewport = [scale, 0, 0, scale, 0, 0];
    } else {
      newWidth = CANVAS_WIDTH;
      newHeight = CANVAS_HEIGHT;
      newViewport = SMALLSCREENVIEWPORT;
    }

    canvas.current.setWidth(newWidth);
    canvas.current.setHeight(newHeight);
    canvas.current.viewportTransform = newViewport;
    canvas.current.getObjects().forEach((obj: any) => {
      obj.setCoords();
    });
    canvas.current.renderAll();
    setIsFullscreen(fullscreen);
  };

  function renderFlipIcon(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    _styleOverride: any,
    fabricObject: any
  ) {
    var size = 24;
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
    ctx.drawImage(flipImg, -size / 2, -size / 2, size, size);
    ctx.restore();
  }

  function flipObject(_eventData: any, transform: any) {
    var group = transform.target.getObjects();
    var img = group.filter((i: any) => i.type == 'image')[0];
    var canvas = transform.target.canvas;

    img.set({
      flipX: img.flipX ? !img.flipX : true,
    });
    return canvas.renderAll();
  }

  //adds the Flip Icon Control
  fabric.Object.prototype.controls.flip = new fabric.Control({
    x: -0.5,
    y: -0.5,
    offsetY: -16,
    offsetX: -16,
    sizeX: 24,
    sizeY: 24,
    cursorStyle: 'pointer',
    visible: true,
    mouseUpHandler: flipObject,
    render: renderFlipIcon,
  });

  // Changing rotation control properties
  function renderRotationIcon(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    _styleOverride: any,
    fabricObject: any
  ) {
    var size = 40;
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
    ctx.drawImage(rotateImg, -size / 2, -size / 2, size, size);
    ctx.restore();
  }

  const originalRotateControl = fabric.Object.prototype.controls.mtr;
  fabric.Object.prototype.controls.mtr = new fabric.Control({
    x: 0,
    y: -0.5,
    offsetX: 0,
    offsetY: -40,
    sizeX: 20,
    sizeY: 20,
    cursorStyle: 'pointer',
    actionHandler: originalRotateControl.actionHandler,
    actionName: 'rotate',
    render: renderRotationIcon,
    withConnection: true,
  });

  //
  // (re-)create canvas if canvasID is changing. This happens between two renders
  // if isEcho is changing - or if you instantiate two React components with
  // a canvas.
  //
  useEffect(() => {
    if (!isEcho) {
      canvas.current = new fabric.Canvas(canvasID, {
        height: CANVAS_HEIGHT,
        width: CANVAS_WIDTH,
        backgroundColor: 'white',
        selection: false,
        renderOnAddRemove: true,
      });
    } else {
      canvas.current = new fabric.StaticCanvas(canvasID, {
        height: CANVAS_HEIGHT,
        width: CANVAS_WIDTH,
        backgroundColor: 'white',
      });
    }

    canvas.current.viewportTransform = SMALLSCREENVIEWPORT;

    initialCharacters.forEach((character, index) => {
      fabric.Image.fromURL(
        mapCharacterIconToFilePath(character.icon as CharacterIcon),
        function (objects: fabric.Image) {
          var obj = objects; // fabric.util.groupSVGElements(objects, options);
          const origHeight = obj.height ? obj.height : 100;
          const origWidth = obj.width ? obj.width : 70;
          const objLeft = obj.left ?? 50;
          const objTop = obj.top ?? 50;

          var text = new fabric.Text(character.name, {
            fontSize: 100,
            fontFamily: 'Roboto',
          });
          const originTextWidth = text.width ? text.width : 60;

          // Setting the text position depending on the objects and text width
          text.set({
            left: objLeft + origWidth / 2 - originTextWidth / 2,
            top: objTop + origHeight,
          });

          var group = new fabric.Group([text, obj], {
            left: 150,
            top: 100,
          });

          // While rotating the text is ungrouped from the image
          group.on('rotating', function (e: any) {
            if (currentText == null) {
              var target = e.transform.target;
              currentText = target
                .getObjects()
                .filter((i: any) => i.type == 'text')[0];
              target.removeWithUpdate(currentText);
              canvas.current.add(currentText);
              currentText.set({ angle: 0 });
              canvas.current.renderAll();
            }
          });

          // When rotating is finished, the text is grouped with the image again
          group.on('modified', function (e: any) {
            var target = e.transform.target;
            canvas.current.remove(currentText);
            target.addWithUpdate(currentText);
            target.set('dirty', true);
            canvas.current.renderAll();
            currentText = null;
          });

          // When a Character is clicked, it is brought to the front (last stack element)
          group.on('mousedown', function (e: any) {
            var target = e.transform.target;
            canvas.current.getObjects().forEach((e: any, index: number) => {
              if (target === e) {
                const numArray = resortedCharacterIndex;
                const movedIndex = numArray.splice(index, 1)[0];
                numArray.push(movedIndex);
                setResortedCharacterIndex(numArray);
              }
            });
            target.bringToFront();
            canvas.current.renderAll();
          });

          if (character.angle) obj.rotate(character.angle);
          obj.set({
            flipX: character.flipX ?? false,
            flipY: character.flipY ?? false,
          });

          group.scale(CHARACTER_SCALE);

          // adapt character position depending on their size
          const halfGroupWidth = group.width
            ? (group.width / 2) * CHARACTER_SCALE
            : 20;
          const halfGroupHeight = group.height
            ? (group.height / 2) * CHARACTER_SCALE
            : 40;
          let calculatedPositionLeft =
            characterPositions[index].x - halfGroupWidth;
          let calculatedPositionTop =
            characterPositions[index].y - halfGroupHeight;

          if (initialCharacters.length > 8) {
            if (index > 3) {
              calculatedPositionTop = calculatedPositionTop + halfGroupHeight;
            }
          }

          group.set({
            left: calculatedPositionLeft,
            top: calculatedPositionTop,
            borderColor: 'black',
            cornerColor: 'black',
            cornerSize: 8,
            cornerStyle: 'circle',
            transparentCorners: false,
          });

          if (character.width) group.scaleToWidth(character.width);
          if (character.height) group.scaleToHeight(character.height);
          if (character.position && character.position.x)
            group.left = character.position.x;
          if (character.position && character.position.y)
            group.top = character.position.y;

          if (character.scale) group.scale(character.scale);

          group.setControlsVisibility({
            ml: false,
            mt: false,
            mb: false,
            mr: false,
          });

          canvas.current.add(group);
        }
      );
    });
    // destroy fabric on unmount
    return () => {
      canvas.current.dispose();
      canvas.current = null;
    };
  }, [canvasID]);

  //adds the initial Characters

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1em',
        alignItems: 'flex-start',
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={isFullscreen ? fullScreenStyle : smallScreenStyle}
        key={canvasID + 'surroundingbox'}
      >
        <FullScreenToggleButton
          isFullscreen={isFullscreen}
          setIsFullscreen={(b: boolean) => toggleFullscreen(b)}
          tooltipFullscreenMessage={tooltipFullscreenMessage}
        ></FullScreenToggleButton>
        <canvas
          id={canvasID}
          style={{
            boxShadow: '6px 6px 6px 6px rgba(0, 0, 0, 0.1)',
            position: 'static',
          }}
        />
      </Box>
      {!isEcho && (
        <>
          {!isFullscreen && (
            <EvoachButton
              onClick={() => {
                send({
                  type: 'writeToContext',
                  payload: {
                    [saveResultTo]: canvas.current
                      .getObjects()
                      .map((group: any, index: number) => {
                        const groupObj = group.getObjects();
                        const object = groupObj.filter(
                          (i: any) => i.type == 'image'
                        )[0];

                        const returnObject = {
                          name:
                            initialCharacters[resortedCharacterIndex[index]]
                              .name,
                          value:
                            initialCharacters[resortedCharacterIndex[index]]
                              .name,
                          type: 'character',
                          icon:
                            initialCharacters[resortedCharacterIndex[index]]
                              .icon,
                          role:
                            initialCharacters[resortedCharacterIndex[index]]
                              .role,
                          statement:
                            initialCharacters[resortedCharacterIndex[index]]
                              .statement,
                          flipX: object.flipX,
                          flipY: object.flipY,
                          scale:
                            object.scaleX == 1 ? group.scaleX : object.scaleX,
                          angle: object.angle,
                          position: {
                            x: group.left,
                            y: group.top,
                          },
                          assetPath: undefined,
                        };
                        return returnObject;
                      }),
                  },
                });
                send({ type: 'next' });
              }}
            >
              {buttonText}
            </EvoachButton>
          )}
        </>
      )}
    </Box>
  );
};

export const renderCanvasEditorAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;

    const initialCharacters = getValueFrom
      ? context.userData[getValueFrom]
      : [];

    newContext.widgetData.push({
      type: 'canvasEditor',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        initialCharacters: initialCharacters,
        buttonText: translateWithFallback(intl, payload.buttonText),
        isEcho: payload.isEcho,
        saveResultTo: payload.saveResultTo,
        tooltipFullscreenMessage: translateWithFallback(
          intl,
          payload.tooltipFullscreenMessage
        ),
      },
    });
    return newContext;
  });
};
